import { useState, useMemo } from 'react'
import Input from '../UI/Input'
import { useQuery, useQueries, useMutation, useQueryClient } from 'react-query'
import Card, { CardHeader, CardTitle, CardContent } from '../UI/Card'
import Button from '../UI/Button'
import LoadingSpinner from '../UI/LoadingSpinner'
import ConfirmationModal from '../Modals/ConfirmationModal'
import SubscriptionModal from '../Modals/SubscriptionModal'
import PaymentModal from '../Modals/PaymentModal'
import ContactSalesModal from '../Modals/ContactSalesModal'
import { CreditCard, Plus, Edit, Trash2, Package, Users, Calendar, DollarSign, ArrowUp, Zap, Database, CheckCircle, Star, RefreshCw } from 'lucide-react'
import toast from 'react-hot-toast'
import api, { plansAPI, usersAPI } from '../../services/api'
import { productsAPI } from '../../services/productsAPI'
import { useAuth } from '../../hooks/useAuth'
import { formatDateUTC, formatDateSimple } from '../../utils/dateUtils'

export default function SubscriptionsTab({ organizationId }) {
  const { hasPermission } = useAuth()
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false)
  const [selectedSubscription, setSelectedSubscription] = useState(null)
  const [showTerminateConfirmation, setShowTerminateConfirmation] = useState(false)
  const [subscriptionToTerminate, setSubscriptionToTerminate] = useState(null)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [selectedUpgradePlan, setSelectedUpgradePlan] = useState(null)
  const [enterpriseCustomPrices, setEnterpriseCustomPrices] = useState({}) // planId -> customPrice
  const [showContactSalesModal, setShowContactSalesModal] = useState(false)
  const [contactSalesPlan, setContactSalesPlan] = useState(null)
  const queryClient = useQueryClient()

  // Fetch subscriptions - optimized caching
  const { data: subscriptionsData, isLoading, error, refetch } = useQuery(
    ['subscriptions', organizationId],
    () => api.get(`/subscriptions/organization/${organizationId}`).then(r => r.data),
    { 
      enabled: !!organizationId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 15 * 60 * 1000,
      refetchOnMount: false,
      onError: (error) => {
        console.error('Error fetching subscriptions:', error)
        toast.error('Failed to load subscriptions')
      }
    }
  )
  const subscriptions = subscriptionsData?.data?.subscriptions || []

  // Fetch organization users to get active user count - optimized caching
  const { data: orgUsersData } = useQuery(
    ['organization-users', organizationId],
    () => usersAPI.getByOrganization(organizationId, { status: 'active' }).then(r => r.data),
    {
      enabled: !!organizationId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 15 * 60 * 1000,
      refetchOnMount: false,
    }
  )
  const allActiveUsers = orgUsersData?.data?.users?.filter(u => u.status === 'active') || []

  // Get unique product IDs from subscriptions that have plans
  const productIds = useMemo(() => {
    const subscriptionsWithPlans = subscriptions.filter(sub => sub.plan_id && sub.product_id)
    return [...new Set(subscriptionsWithPlans.map(sub => sub.product_id))]
  }, [subscriptions])

  // Fetch all plans for products with subscriptions using useQueries - optimized caching
  const plansQueries = useQueries(
    productIds.map(productId => ({
      queryKey: ['plans', productId, 'upgrade'],
      queryFn: () => plansAPI.getByProduct(productId).then(r => r.data),
      enabled: !!productId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 15 * 60 * 1000,
      refetchOnMount: false,
      onError: (error) => {
        if (error.response?.status !== 404) {
          console.error('Error fetching plans for product:', productId, error)
        }
      }
    }))
  )

  // Fetch product details for plan cards (modules and features) - optimized caching
  const productDetailsQueries = useQueries(
    productIds.map(productId => ({
      queryKey: ['product-details', productId, 'upgrade'],
      queryFn: () => productsAPI.getDetails(productId).then(r => r.data),
      enabled: !!productId,
      staleTime: 5 * 60 * 1000,
      cacheTime: 15 * 60 * 1000,
      refetchOnMount: false,
      onError: (error) => {
        console.error('Error fetching product details:', error)
      }
    }))
  )

  // Create a map of product_id -> plans (sorted by display_order)
  const plansByProduct = useMemo(() => {
    const plansMap = {}
    productIds.forEach((productId, index) => {
      const plansData = plansQueries[index]?.data
      if (plansData?.data?.plans) {
        // Sort plans by display_order
        const sortedPlans = (plansData.data.plans || []).sort((a, b) => {
          const orderA = a.display_order ?? null
          const orderB = b.display_order ?? null
          
          if (orderA === null && orderB === null) {
            return new Date(b.created_at || 0) - new Date(a.created_at || 0)
          }
          if (orderA === null) return 1
          if (orderB === null) return -1
          
          if (orderA !== orderB) {
            return orderA - orderB
          }
          
          return new Date(b.created_at || 0) - new Date(a.created_at || 0)
        })
        plansMap[productId] = sortedPlans
      }
    })
    return plansMap
  }, [productIds, plansQueries])

  // Create a map of product_id -> product details (modules and features)
  const productDetailsByProduct = useMemo(() => {
    const detailsMap = {}
    productIds.forEach((productId, index) => {
      const productData = productDetailsQueries[index]?.data
      if (productData?.data?.data?.product) {
        detailsMap[productId] = {
          modules: productData.data.data.product.modules || [],
          features: productData.data.data.product.features || []
        }
      }
    })
    return detailsMap
  }, [productIds, productDetailsQueries])

  // Get upgrade plans for a subscription (only higher display_order)
  const getUpgradePlans = (subscription) => {
    if (!subscription.plan_id || !subscription.product_id) return []
    
    const allPlans = plansByProduct[subscription.product_id] || []
    // Compare UUIDs as strings to ensure proper matching
    const currentPlan = allPlans.find(p => String(p.id) === String(subscription.plan_id))
    
    if (!currentPlan || currentPlan.display_order === null || currentPlan.display_order === undefined) {
      return []
    }
    
    // Filter plans with higher display_order (upgrade options only)
    const upgradePlans = allPlans.filter(plan => {
      if (!plan.display_order || String(plan.id) === String(subscription.plan_id)) return false
      return plan.display_order > currentPlan.display_order
    })
    
    // Sort by display_order (ascending)
    return upgradePlans.sort((a, b) => {
      const orderA = a.display_order ?? null
      const orderB = b.display_order ?? null
      
      if (orderA === null && orderB === null) return 0
      if (orderA === null) return 1
      if (orderB === null) return -1
      
      return orderA - orderB
    })
  }

  // Terminate subscription mutation
  const terminateMutation = useMutation(
    (subscriptionId) => api.delete(`/subscriptions/${subscriptionId}`).then(r => r.data),
    {
      onSuccess: (result) => {
        if (result?.success) {
          toast.success('Subscription terminated successfully!')
          queryClient.invalidateQueries(['subscriptions', organizationId])
          queryClient.invalidateQueries(['payments', organizationId])
        } else {
          toast.error(result?.message || 'Failed to terminate subscription')
        }
      },
      onError: () => {
        toast.error('Failed to terminate subscription')
      }
    }
  )


  const handleAddSubscription = () => {
    setSelectedSubscription(null)
    setShowSubscriptionModal(true)
  }

  const handleEditSubscription = (subscription) => {
    setSelectedSubscription(subscription)
    setShowSubscriptionModal(true)
  }

  const handleSubscriptionSuccess = () => {
    setShowSubscriptionModal(false)
    setSelectedSubscription(null)
    queryClient.invalidateQueries(['subscriptions', organizationId])
    queryClient.invalidateQueries(['payments', organizationId])
    // Toast is shown by SubscriptionModal (created/updated) to avoid duplicate messages
  }

  // Check if a plan is free (both monthly and yearly prices are 0)
  const isPlanFree = (plan) => {
    if (!plan) return false
    const monthlyPrice = parseFloat(plan.monthly_price) || 0
    const yearlyPrice = parseFloat(plan.yearly_price) || 0
    return monthlyPrice === 0 && yearlyPrice === 0
  }

  // Check if a subscription's plan is free (for display: show "Free" instead of "Monthly"/"Yearly")
  const isSubscriptionPlanFree = (subscription) => {
    const plans = plansByProduct[subscription?.product_id] || []
    const plan = plans.find(p => p.id === subscription?.plan_id)
    return isPlanFree(plan)
  }

  // Mutation to upgrade subscription directly (for free plans)
  const upgradeSubscriptionMutation = useMutation(
    ({ subscriptionId, planId, billingCycle }) => 
      api.put(`/subscriptions/${subscriptionId}`, {
        plan_id: planId,
        billing_cycle: billingCycle,
        status: 'active'
      }).then(r => r.data),
    {
      onSuccess: (result) => {
        if (result?.success) {
          toast.success('Subscription upgraded successfully!')
          queryClient.invalidateQueries(['subscriptions', organizationId])
          queryClient.invalidateQueries(['payments'])
        } else {
          toast.error(result?.message || 'Failed to upgrade subscription')
        }
      },
      onError: (error) => {
        console.error('Upgrade error:', error)
        toast.error(error.response?.data?.message || 'Failed to upgrade subscription')
      }
    }
  )

  const handleUpgradeClick = (subscription, upgradePlan) => {
    setSelectedSubscription(subscription)
    setSelectedUpgradePlan(upgradePlan)
    
    // Check if the upgrade plan is free
    if (isPlanFree(upgradePlan)) {
      // Free plan - upgrade directly without payment modal
      const billingCycle = subscription.billing_cycle || 'monthly'
      upgradeSubscriptionMutation.mutate({
        subscriptionId: subscription.id,
        planId: upgradePlan.id,
        billingCycle
      })
    } else {
      // Paid plan - show payment modal
      setShowPaymentModal(true)
    }
  }

  const handleEnterpriseCustomPriceChange = (planId, value) => {
    setEnterpriseCustomPrices(prev => ({
      ...prev,
      [planId]: value ? parseFloat(value) : null
    }))
  }

  const handleContactSalesClick = (plan, customPrice = null) => {
    setContactSalesPlan({ ...plan, customPrice })
    setShowContactSalesModal(true)
  }

  // Renew subscription mutation
  const renewMutation = useMutation(
    ({ subscriptionId, billingCycle }) => 
      api.post(`/subscriptions/${subscriptionId}/renew`, { billing_cycle: billingCycle }).then(r => r.data),
    {
      onSuccess: (result) => {
        if (result?.success) {
          toast.success('Subscription renewed successfully!')
          queryClient.invalidateQueries(['subscriptions', organizationId])
          queryClient.invalidateQueries(['payments'])
        } else {
          toast.error(result?.message || 'Failed to renew subscription')
        }
      },
      onError: (error) => {
        console.error('Renewal error:', error)
        toast.error(error.response?.data?.message || 'Failed to renew subscription')
      }
    }
  )

  const handleRenewClick = (subscription) => {
    // Set subscription and show payment modal for renewal
    setSelectedSubscription(subscription)
    setSelectedUpgradePlan(null) // No upgrade plan for renewal
    setShowPaymentModal(true)
  }

  const handlePaymentSuccess = (paymentData) => {
    setShowPaymentModal(false)
    setSelectedSubscription(null)
    // Clear custom price for the upgraded plan if it was Enterprise
    if (selectedUpgradePlan?.is_enterprise && selectedUpgradePlan?.id) {
      setEnterpriseCustomPrices(prev => {
        const updated = { ...prev }
        delete updated[selectedUpgradePlan.id]
        return updated
      })
    }
    setSelectedUpgradePlan(null)
    queryClient.invalidateQueries(['subscriptions', organizationId])
    queryClient.invalidateQueries(['payments'])
    
    // If payment is completed, the backend should have already updated the subscription
    if (paymentData?.data?.payment?.payment_status === 'completed' && paymentData?.data?.payment?.plan_id) {
      toast.success('Payment processed successfully! Subscription upgraded.')
    } else {
      toast.success('Payment recorded successfully!')
    }
  }

  const handleTerminateSubscription = (subscription) => {
    setSubscriptionToTerminate(subscription)
    setShowTerminateConfirmation(true)
  }


  const confirmTerminateSubscription = () => {
    if (subscriptionToTerminate) {
      terminateMutation.mutate(subscriptionToTerminate.id)
      setShowTerminateConfirmation(false)
      setSubscriptionToTerminate(null)
    }
  }

  const cancelTerminateSubscription = () => {
    setShowTerminateConfirmation(false)
    setSubscriptionToTerminate(null)
  }

  // Check if user has permission to view organization data
  if (!hasPermission('organizations.read')) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
          <p className="text-gray-600">You don't have permission to view subscriptions.</p>
        </div>
      </div>
    )
  }

  const getStatusBadge = (status, isExpired = false) => {
    // If expired, show expired badge regardless of status
    if (isExpired) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          Expired
        </span>
      )
    }
    
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: 'Active' },
      inactive: { color: 'bg-gray-100 text-gray-800', label: 'Inactive' },
      cancelled: { color: 'bg-red-100 text-red-800', label: 'Cancelled' }
    }
    
    const config = statusConfig[status] || statusConfig.inactive
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    )
  }

  const getBillingCycleBadge = (cycle, subscription) => {
    const isFree = subscription ? isSubscriptionPlanFree(subscription) : false
    if (isFree) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Free
        </span>
      )
    }
    const cycleConfig = {
      monthly: { color: 'bg-blue-100 text-blue-800', label: 'Monthly' },
      yearly: { color: 'bg-purple-100 text-purple-800', label: 'Yearly' }
    }
    const config = cycleConfig[cycle] || cycleConfig.monthly
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    )
  }

  const formatPrice = (price) => {
    if (price === null || price === undefined || isNaN(price)) {
      return '$0.00'
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price)
  }

  // Calculate due date based on billing cycle (using UTC for consistency)
  const calculateDueDate = (subscription) => {
    if (!subscription.billing_cycle || !subscription.created_at) {
      return null
    }

    try {
      const startDate = new Date(subscription.created_at)
      // Create date in UTC, starting from the date part only (ignore time)
      const dueDate = new Date(Date.UTC(
        startDate.getUTCFullYear(),
        startDate.getUTCMonth(),
        startDate.getUTCDate(),
        0, 0, 0, 0 // Set to midnight UTC
      ))

      if (subscription.billing_cycle === 'monthly') {
        // Add 1 month using UTC methods
        dueDate.setUTCMonth(dueDate.getUTCMonth() + 1)
      } else if (subscription.billing_cycle === 'yearly') {
        // Add 1 year using UTC methods
        dueDate.setUTCFullYear(dueDate.getUTCFullYear() + 1)
      } else {
        return null
      }

      return dueDate
    } catch (error) {
      console.error('Error calculating due date:', error)
      return null
    }
  }

  // Calculate price for a subscription based on active users
  const calculateSubscriptionPrice = (subscription) => {
    if (!subscription.plan_id || !subscription.product_id) return null

    // Get the plan from plansByProduct
    const plans = plansByProduct[subscription.product_id] || []
    const plan = plans.find(p => p.id === subscription.plan_id)
    if (!plan) return subscription.calculated_price

    // Get base price based on billing cycle
    let basePrice = 0
    if (subscription.billing_cycle === 'monthly') {
      basePrice = parseFloat(plan.monthly_price) || 0
    } else if (subscription.billing_cycle === 'yearly') {
      basePrice = parseFloat(plan.yearly_price) || 0
    } else {
      basePrice = parseFloat(plan.monthly_price) || 0
    }

    if (basePrice === 0) return subscription.calculated_price

    // If pricing model is per_user, multiply by active users subscribed to this product
    if (subscription.pricing_model === 'per_user') {
      // Find all subscriptions for this product
      const productSubscriptions = subscriptions.filter(sub => 
        sub.product_id === subscription.product_id
      )
      
      // Get subscription IDs for this product
      const productSubscriptionIds = productSubscriptions.map(sub => sub.id)
      
      // Count active users who have these subscription IDs
      const productActiveUsers = allActiveUsers.filter(user => 
        user.subscription_id && productSubscriptionIds.includes(user.subscription_id)
      )
      
      const activeUserCount = productActiveUsers.length
      if (activeUserCount > 0) {
        return basePrice * activeUserCount
      }
      return null
    }

    // For fixed pricing, return base price
    return basePrice
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Subscriptions</h3>
          <p className="text-gray-600 mb-4">
            {error?.response?.data?.message || error?.message || 'Failed to load subscriptions'}
          </p>
          <Button onClick={() => refetch()}>
            Try Again
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-secondary-900">Subscriptions</h3>
          <p className="text-sm text-secondary-600">
            Manage product subscriptions for this organization
          </p>
        </div>
        {hasPermission('organizations.create') && (
          <Button onClick={handleAddSubscription}>
            <Plus className="h-4 w-4 mr-2" />
            Add Subscription
          </Button>
        )}
      </div>

      {/* Subscriptions List */}
      {subscriptions.length > 0 ? (
        <div className="space-y-4">
          {subscriptions.map((subscription) => (
            <Card key={subscription.id}>
              <CardContent className="p-6">
                {/* Current Subscription Details - Top Section */}
                <div className="relative">
                  {/* Edit and Delete Buttons - Top Right */}
                  <div className="absolute top-0 right-0 flex items-center space-x-2">
                    {hasPermission('organizations.update') && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditSubscription(subscription)}
                        className="h-8 w-8 p-0"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    )}
                    {hasPermission('organizations.delete') && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTerminateSubscription(subscription)}
                        disabled={terminateMutation.isLoading}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                        title="Terminate Subscription"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  {/* Service Name */}
                  <h4 className="text-lg font-semibold text-secondary-900 mb-3 pr-20">
                    {subscription.product_name}
                  </h4>

                  {/* Status and Billing Cycle Badges */}
                  <div className="flex items-center gap-2 mb-3">
                    {getStatusBadge(subscription.status, subscription.is_expired)}
                    {getBillingCycleBadge(subscription.billing_cycle, subscription)}
                  </div>

                  {/* Description */}
                  <p className="text-sm text-secondary-600 mb-4">
                    {subscription.product_description || 'No description available'}
                  </p>

                  {/* Subscription Details Grid - 2 columns */}
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm mb-4">
                    <div>
                      <span className="text-secondary-600">User Limit:</span>
                      <span className="ml-2 font-medium text-secondary-900">
                        {subscription.user_limit !== null && subscription.user_limit !== undefined 
                          ? `${subscription.user_limit} users`
                          : 'Unlimited users'}
                      </span>
                    </div>
                    {subscription.pricing_model && (
                      <div>
                        <span className="text-secondary-600">Pricing Model:</span>
                        <span className="ml-2 font-medium text-secondary-900 capitalize">{subscription.pricing_model}</span>
                      </div>
                    )}
                    {subscription.plan_name && (
                      <div>
                        <span className="text-secondary-600">Plan:</span>
                        <span className="ml-2 font-medium text-secondary-900">{subscription.plan_name}</span>
                      </div>
                    )}
                    {(() => {
                      const calculatedPrice = calculateSubscriptionPrice(subscription)
                      const isFree = isSubscriptionPlanFree(subscription)
                      return calculatedPrice !== null && calculatedPrice !== undefined ? (
                        <div>
                          <span className="text-secondary-600">Total Price:</span>
                          <span className="ml-2 font-medium text-secondary-900">
                            {isFree ? 'Free' : `${formatPrice(calculatedPrice)} / ${subscription.billing_cycle || 'monthly'}`}
                          </span>
                        </div>
                      ) : null
                    })()}
                    {subscription.created_at && (
                      <div>
                        <span className="text-secondary-600">Subscription Date:</span>
                        <span className="ml-2 font-medium text-secondary-900">
                          {formatDateSimple(subscription.created_at) || new Date(subscription.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                    {(() => {
                      // Use due_date from API if available, otherwise calculate it
                      let dueDate = null
                      if (subscription.due_date) {
                        // Use the due_date from the API (already calculated based on trial days or billing cycle)
                        dueDate = subscription.due_date
                      } else {
                        // Fallback to calculation if due_date is not provided
                        const calculated = calculateDueDate(subscription)
                        dueDate = calculated ? calculated.toISOString() : null
                      }
                      return dueDate ? (
                        <div>
                          <span className="text-secondary-600">Due Date:</span>
                          <span className="ml-2 font-medium text-secondary-900">
                            {formatDateSimple(dueDate) || new Date(dueDate).toLocaleDateString()}
                          </span>
                        </div>
                      ) : null
                    })()}
                  </div>
                </div>

                {/* Expired Subscription Notice */}
                {subscription.is_expired && (
                  <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <Calendar className="h-5 w-5 text-yellow-600" />
                      </div>
                      <div className="ml-3 flex-1">
                        <h5 className="text-sm font-medium text-yellow-800">
                          Subscription Expired
                        </h5>
                        <p className="mt-1 text-sm text-yellow-700">
                          {subscription.is_free_plan 
                            ? 'Your free plan has expired. Please upgrade to a paid plan to continue using the service.'
                            : 'Your subscription has expired. You can renew your current plan or upgrade to a different plan.'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Upgrade/Renewal Options Section */}
                {(() => {
                  const isExpired = subscription.is_expired
                  const isFreePlan = subscription.is_free_plan
                  
                  // For expired free plans: show all available plans (not just upgrades)
                  // For expired paid plans: show upgrade plans + renew option
                  // For active subscriptions: show only upgrade plans
                  let availablePlans = []
                  if (isExpired && isFreePlan) {
                    // Show all plans for expired free plans
                    const allPlans = plansByProduct[subscription.product_id] || []
                    availablePlans = allPlans.filter(plan => 
                      plan.status === 'active' && String(plan.id) !== String(subscription.plan_id)
                    )
                  } else {
                    // Show upgrade plans (or all if expired paid)
                    availablePlans = isExpired 
                      ? (plansByProduct[subscription.product_id] || []).filter(plan => 
                          plan.status === 'active' && String(plan.id) !== String(subscription.plan_id)
                        )
                      : getUpgradePlans(subscription)
                  }
                  
                  const productDetails = productDetailsByProduct[subscription.product_id] || { modules: [], features: [] }
                  
                  // Helper function to get module names from IDs
                  const getModuleNames = (plan) => {
                    let moduleIds = plan.included_modules || plan.module_ids || []
                    if (typeof moduleIds === 'string') {
                      try {
                        moduleIds = JSON.parse(moduleIds)
                      } catch (e) {
                        moduleIds = []
                      }
                    }
                    if (!Array.isArray(moduleIds)) moduleIds = []
                    return moduleIds
                      .map(moduleId => {
                        const module = productDetails.modules.find(m => String(m.id) === String(moduleId))
                        return module ? module.name : null
                      })
                      .filter(Boolean)
                  }

                  // Helper function to get feature names from IDs
                  const getFeatureNames = (plan) => {
                    let featureIds = plan.included_features || plan.feature_ids || []
                    if (typeof featureIds === 'string') {
                      try {
                        featureIds = JSON.parse(featureIds)
                      } catch (e) {
                        featureIds = []
                      }
                    }
                    if (!Array.isArray(featureIds)) featureIds = []
                    return featureIds
                      .map(featureId => {
                        const feature = productDetails.features.find(f => String(f.id) === String(featureId))
                        return feature ? feature.name : null
                      })
                      .filter(Boolean)
                  }
                  
                  // Show options if expired or if there are upgrade plans
                  if (isExpired || availablePlans.length > 0) {
                    return (
                      <div className="mt-6 pt-6 border-t border-secondary-200">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h5 className="text-lg font-bold text-secondary-900 mb-1">
                              {isExpired 
                                ? (isFreePlan ? 'Available Plans' : 'Renewal & Upgrade Options')
                                : 'Upgrade Options'
                              }
                            </h5>
                            <p className="text-sm text-secondary-600">
                              {isExpired 
                                ? (isFreePlan 
                                    ? 'Choose a plan to continue using the service'
                                    : 'Renew your current plan or upgrade to a different plan'
                                  )
                                : 'Select a plan to upgrade your subscription'
                              }
                            </p>
                          </div>
                          {/* Renew Button for expired paid plans */}
                          {isExpired && !isFreePlan && (
                            <Button
                              onClick={() => handleRenewClick(subscription)}
                              disabled={renewMutation.isLoading}
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              {renewMutation.isLoading ? (
                                <>
                                  <LoadingSpinner size="sm" className="mr-2" />
                                  Renewing...
                                </>
                              ) : (
                                <>
                                  <Calendar className="h-4 w-4 mr-2" />
                                  Renew Current Plan
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                        
                        {availablePlans.length > 0 && (
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {availablePlans.map((plan) => {
                            // Use base prices from plan (not multiplied by users for display)
                            const monthlyPrice = plan.monthly_price || 0
                            const yearlyPrice = plan.yearly_price || 0
                            
                            // Calculate savings percentage
                            const calculateSavings = () => {
                              if (!monthlyPrice || !yearlyPrice || monthlyPrice === 0) return null
                              const monthlyYearlyTotal = monthlyPrice * 12
                              if (yearlyPrice >= monthlyYearlyTotal) return null
                              const savings = monthlyYearlyTotal - yearlyPrice
                              const savingsPercent = Math.round((savings / monthlyYearlyTotal) * 100)
                              return savingsPercent > 0 ? savingsPercent : null
                            }
                            
                            const savingsPercent = calculateSavings()
                            const moduleNames = getModuleNames(plan)
                            const featureNames = getFeatureNames(plan)
                            
                            return (
                              <Card 
                                key={plan.id} 
                                className="relative flex flex-col border border-secondary-200 hover:border-blue-300 hover:shadow-lg hover:scale-105 transition-all duration-200 bg-white shadow-sm cursor-pointer"
                                onClick={() => {
                                  if (plan.is_enterprise) {
                                    // For enterprise plans, open contact us modal
                                    const customPrice = enterpriseCustomPrices[plan.id]
                                    handleContactSalesClick(plan, customPrice)
                                  } else {
                                    // For regular plans, trigger upgrade
                                    handleUpgradeClick(subscription, plan)
                                  }
                                }}
                              >
                                {/* Most Popular Badge - Top Right Edge */}
                                {plan.is_popular && (
                                  <div className="absolute top-0 right-0 z-10">
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-bl text-xs font-medium bg-blue-500 text-white">
                                      Most Popular
                                    </span>
                                  </div>
                                )}
                                <CardContent className="p-6 flex flex-col flex-1">
                                  {/* Plan Header */}
                                  <div className="mb-4">
                                    <div className="flex items-center space-x-2 mb-2">
                                      <h6 className="text-xl font-bold text-secondary-900 capitalize">{plan.name}</h6>
                                      {plan.is_enterprise && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-300 text-white">
                                          Enterprise
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  {/* Pricing - Large and prominent */}
                                  {!plan.is_enterprise && monthlyPrice > 0 && (
                                    <div className="mb-4">
                                      <div className="text-2xl font-bold text-secondary-900">
                                        {new Intl.NumberFormat('en-US', {
                                          style: 'currency',
                                          currency: plan.currency || 'USD'
                                        }).format(monthlyPrice)}
                                        <span className="text-base font-normal text-secondary-500 ml-1">
                                          /month
                                        </span>
                                      </div>
                                      {yearlyPrice > 0 && (
                                        <div className="text-sm text-secondary-600 mt-1">
                                          {new Intl.NumberFormat('en-US', {
                                            style: 'currency',
                                            currency: plan.currency || 'USD'
                                          }).format(yearlyPrice)}/year
                                          {savingsPercent && (
                                            <span className="text-green-600 ml-1">(save {savingsPercent}%)</span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )}


                                  {/* Plan Details Section - Simplified with key features */}
                                  <div className="flex-1 mb-4">
                                    {/* Key Features - Users, API Calls, Storage - Always show */}
                                    <div className="space-y-2">
                                      {/* Users - Always show */}
                                      <div className="flex items-center text-sm">
                                        <Users className="h-4 w-4 text-secondary-400 mr-2 flex-shrink-0" />
                                        <span className="text-secondary-600">
                                          {plan.max_users !== null && plan.max_users !== undefined && plan.max_users > 0
                                            ? `${plan.max_users.toLocaleString()} users`
                                            : 'Unlimited users'}
                                        </span>
                                      </div>
                                      
                                      {/* API Calls - Always show */}
                                      <div className="flex items-center text-sm">
                                        <Zap className="h-4 w-4 text-secondary-400 mr-2 flex-shrink-0" />
                                        <span className="text-secondary-600">
                                          {plan.max_api_calls !== null && plan.max_api_calls !== undefined && plan.max_api_calls > 0
                                            ? `${plan.max_api_calls.toLocaleString()} API calls/month`
                                            : 'Unlimited API calls'}
                                        </span>
                                      </div>
                                      
                                      {/* Storage - Always show */}
                                      <div className="flex items-center text-sm">
                                        <Database className="h-4 w-4 text-secondary-400 mr-2 flex-shrink-0" />
                                        <span className="text-secondary-600">
                                          {(() => {
                                            // Get storage value from plan, checking multiple possible sources
                                            const storageGb = plan.max_storage_gb !== undefined 
                                              ? plan.max_storage_gb 
                                              : (plan.features?.max_storage_gb !== undefined 
                                                ? plan.features.max_storage_gb 
                                                : null)
                                            
                                            // Check if storage is explicitly set and greater than 0
                                            if (storageGb !== null && storageGb !== undefined && !isNaN(storageGb) && storageGb > 0) {
                                              return `${parseInt(storageGb)} GB storage`
                                            }
                                            // Otherwise show unlimited
                                            return 'Unlimited storage'
                                          })()}
                                        </span>
                                      </div>
                                      
                                      {/* Modules Count - Always show */}
                                      {(() => {
                                        // Get module count directly from plan's included_modules
                                        let moduleIds = plan.included_modules || plan.module_ids || []
                                        if (typeof moduleIds === 'string') {
                                          try {
                                            moduleIds = JSON.parse(moduleIds)
                                          } catch (e) {
                                            moduleIds = []
                                          }
                                        }
                                        if (!Array.isArray(moduleIds)) moduleIds = []
                                        const moduleCount = moduleIds.length
                                        
                                        return (
                                          <div className="flex items-center text-sm">
                                            <Package className="h-4 w-4 text-secondary-400 mr-2 flex-shrink-0" />
                                            <span className="text-secondary-600">
                                              {moduleCount > 0
                                                ? `${moduleCount} ${moduleCount === 1 ? 'module' : 'modules'}`
                                                : '0 modules'}
                                            </span>
                                          </div>
                                        )
                                      })()}
                                    </div>
                                  </div>

                                  {/* Upgrade Button - White background with grey border (only for non-enterprise plans) */}
                                  {!plan.is_enterprise && (
                                    <Button
                                      onClick={(e) => {
                                        e.stopPropagation() // Prevent card click from firing
                                        handleUpgradeClick(subscription, plan)
                                      }}
                                      className="w-full mt-auto bg-white border border-secondary-300 text-secondary-900 hover:bg-secondary-50 hover:border-secondary-400"
                                      variant="outline"
                                    >
                                      Upgrade to {plan.name}
                                    </Button>
                                  )}
                                </CardContent>
                              </Card>
                            )
                          })}
                          </div>
                        )}
                      </div>
                    )
                  }
                  return null
                })()}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="text-center py-12">
            <CreditCard className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-secondary-900 mb-2">No Subscriptions</h3>
            <p className="text-secondary-600 mb-4">
              This organization doesn't have any active subscriptions yet.
            </p>
            {hasPermission('organizations.update') && (
              <Button onClick={handleAddSubscription}>
                <CreditCard className="h-4 w-4 mr-2" />
                Add Subscription
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => {
          setShowSubscriptionModal(false)
          setSelectedSubscription(null)
        }}
        organizationId={organizationId}
        subscription={selectedSubscription}
        onSuccess={handleSubscriptionSuccess}
      />

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false)
          setSelectedSubscription(null)
          setSelectedUpgradePlan(null)
        }}
        subscription={selectedSubscription}
        upgradePlan={selectedUpgradePlan}
        isRenewal={selectedSubscription && !selectedUpgradePlan}
        onSuccess={handlePaymentSuccess}
      />

      {/* Terminate Confirmation Modal */}
      <ConfirmationModal
        isOpen={showTerminateConfirmation}
        onClose={cancelTerminateSubscription}
        onConfirm={confirmTerminateSubscription}
        title="Terminate Subscription"
        message={`Are you sure you want to terminate the subscription for "${subscriptionToTerminate?.product_name}"? This action cannot be undone.`}
        confirmText="Terminate"
        cancelText="Cancel"
        type="danger"
      />

      {/* Contact Us Modal */}
      <ContactSalesModal
        isOpen={showContactSalesModal}
        onClose={() => {
          setShowContactSalesModal(false)
          setContactSalesPlan(null)
        }}
        plan={contactSalesPlan}
        organizationId={organizationId}
      />
    </div>
  )
}
